import { useEffect, useState, useMemo, useRef } from "react";
import ReportModal from "../ReportModal.component";
import ReportService from "../../../services/reports/Report.service";
import BaseReport from "../BaseReport.component"; // still used for export buttons
import { OtbRecapRawData, OtbRecapParsedData } from "../../../interfaces/reports/OtbRecapReport.interface";
import { MoneyDataItem } from "../../../interfaces/reports/BaseReport.interface";
import TipContext from "../../../providers/GlobalTip";
import EpochMonthService from "../../../services/EpochMonth.service";
import MonthSelector from "../../../tailwindComponents/ui/dates/month-selector.component";
import ReportTable from "../ReportTable.component";
import OtbRecapReportService from "../../../services/reports/OtbRecapTableReport.service";
import MultiSelect from "../../../tailwindComponents/ui/multi-select";

interface OtbRecapTableReportProps { clientId: string | number; epochMonth: number; monthVersion?: 'MONTH_TO_DATE' | 'MONTH_END'; }

interface IndexCalcItem {
    locationCode: string;
    classCode: string;
    parentClientClassCode: string | null;
    sales: number[];
    salesPlan: number[];
    costComplement: number;
    costComplementMtd: number;
    inflowUpdated: number[];
    onOrder: number[];
    trend: number;
    onOrderCost: number[];
    inflowPlan: number[];
    inflowCostPlan: number[];
    inventoryBomPlanNM: number[];
    markdownsRemaining: number[];
    inventoryCurrent: number[];   
    invDeviation: number[];
    invDeviationCost: number[];
    // Pre-calculated OTB values at class level
    inflowTrendRetail: number[];
    inflowTrendCost: number[];
    otbTrendRetail: number[];
    otbTrendCost: number[];
    otbUpdatedRetail: number[];
    otbUpdatedCost: number[];
}
interface IndexCalcData {
    [key: string]: IndexCalcItem;
}

const HIERARCHY_OPTIONS = {
    "By Location": ["vertical", "locationCode"],
    "Location By Class": ["vertical", "locationCode", "classCode"],
    "By Class": ["vertical", "classCode"],
    "By Category": ["vertical", "category"],
    "Location By Category": ["vertical", "locationCode", "category"],
    "Class by Location": ["vertical", "classCode", "locationCode"],
    "Category by Location": ["vertical", "category", "locationCode"],
    "Category by Class": ["vertical", "category", "classCode"],
    "Category by Class by Location": ["vertical", "category", "classCode", "locationCode"]
};

const normalizeCategorySortOrder = (value: unknown): number | null => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
}

const normalizeCategory = (value: unknown): string => {
    if (typeof value !== 'string') return 'Uncategorized';
    const normalized = value.trim();
    return normalized !== '' ? normalized : 'Uncategorized';
}

const buildCategorySortOrderMap = (raw: OtbRecapRawData[]): Record<string, number> => {
    const orderMap: Record<string, number> = {};

    for (const item of raw) {
        const category = normalizeCategory(item.category);
        if (category === 'Uncategorized') continue;

        const order = normalizeCategorySortOrder(item.categorySortOrder);
        if (order == null) continue;

        if (!(category in orderMap) || order < orderMap[category]) {
            orderMap[category] = order;
        }
    }

    return orderMap;
}

// Helper: parse raw data into OtbRecapParsedData[]
function parseOtbRecapRawData(raw: OtbRecapRawData[]): OtbRecapParsedData[] {
    const categorySortOrderMap = buildCategorySortOrderMap(raw);

    return raw.map((item) => {
        let monthData = {} as { [key: string]: MoneyDataItem };
        for (let m = 1; m <= 12; m++) {
            const monthValue = (item as any)[`month${m}`];
            if (monthValue === undefined) break;
            monthData[`month${m}`] = new MoneyDataItem(monthValue ?? 0);
        }

        const normalizedCategory = normalizeCategory(item.category);
        const rawCategorySortOrder = normalizeCategorySortOrder(item.categorySortOrder);
        const hydratedCategorySortOrder = rawCategorySortOrder ?? categorySortOrderMap[normalizedCategory] ?? null;
        const categorySortOrder = normalizedCategory === 'Uncategorized' ? Number.MAX_SAFE_INTEGER : hydratedCategorySortOrder;

        // Remove original month1..month12 from item to avoid type conflict
        const {
            month1, month2, month3, month4, month5, month6,
            month7, month8, month9, month10, month11, month12,
            ...rest
        } = item;
        return {
            ...rest,
            category: normalizedCategory,
            parentClientClassCode: item.parentClientClassCode ?? null,
            categorySortOrder,
            ...monthData,
        } as OtbRecapParsedData;
    });
}

function monthXIndexesToArray(item: OtbRecapParsedData): number[] {
    let arr: number[] = [];
    for (let m = 1; m <= 12; m++) {
        const monthValue = (item as any)[`month${m}`];
        if (monthValue === undefined) break;
        arr.push(monthValue?.value ?? 0);
    }
    return arr;
}

// Build index calculation data from parsed data
function buildIndexCalcData(parsed: OtbRecapParsedData[], customTrend: number): IndexCalcData {
    let indexData: IndexCalcData = {};

    const addToArray = (target: number[], source: number[]) => {
        for (let i = 0; i < source.length; i++) {
            target[i] = (target[i] || 0) + (source[i] || 0);
        }
        return target;
    }

    for (let item of parsed) {
        if (item.locationCode == '' || item.classCode == '') {
            continue;
        }

        // key for each hierarchy
        const keys = [
            // By Location
            `Loc${item.locationCode}`,
            // Location By Class
            `Loc${item.locationCode}|${item.classCode}`,
            // By Class
            `${item.classCode}`,
            // By Category
            `Cat${item.category}`,
            // Location By Category
            `Loc${item.locationCode}|Cat${item.category}`,
            // Class by Location
            `${item.classCode}|Loc${item.locationCode}`,
            // Category by Location
            `Cat${item.category}|Loc${item.locationCode}`,
            // Category by Class
            `Cat${item.category}|${item.classCode}`,
            // Category by Class by Location
            `Cat${item.category}|${item.classCode}|Loc${item.locationCode}`,
            // Overall total
            `|||`,
        ];

        let arr: number[] = [];

        const addToArray = (target: number[], source: number[]) => {
            for (let i = 0; i < source.length; i++) {
                target[i] = (target[i] || 0) + (source[i] || 0);
            }

            return target;
        }

        for (const key of keys) {
            if (!indexData[key]) {
                indexData[key] = {
                    locationCode: item.locationCode,
                    classCode: item.classCode,
                    parentClientClassCode: item.parentClientClassCode ?? null,
                    sales: Array(12).fill(0),
                    salesPlan: Array(12).fill(0),
                    costComplement: item.costComplement ?? 1,
                    costComplementMtd: item.costComplementMtd ?? 1,
                    onOrder: Array(12).fill(0),
                    trend: item.trend && item.trend != 1 ? customTrend : 1,
                    onOrderCost: Array(12).fill(0),
                    inflowUpdated: Array(12).fill(0),
                    inflowPlan: Array(12).fill(0),
                    inflowCostPlan: Array(12).fill(0),
                    inventoryBomPlanNM: Array(12).fill(0),
                    markdownsRemaining: Array(12).fill(0),
                    inventoryCurrent: Array(12).fill(0),
                    invDeviation: Array(12).fill(0),
                    invDeviationCost: Array(12).fill(0),
                    inflowTrendRetail: Array(12).fill(0),
                    inflowTrendCost: Array(12).fill(0),
                    otbTrendRetail: Array(12).fill(0),
                    otbTrendCost: Array(12).fill(0),
                    otbUpdatedRetail: Array(12).fill(0),
                    otbUpdatedCost: Array(12).fill(0),
                };
            }

            arr = monthXIndexesToArray(item);

            switch (item.vertical) {
                case 'sales':
                    indexData[key].sales = addToArray(indexData[key].sales, arr);
                    break;
                case 'Sales Plan':
                    indexData[key].salesPlan = addToArray(indexData[key].salesPlan, arr);
                    break;
                case 'On Order Retail':
                    indexData[key].onOrder = addToArray(indexData[key].onOrder, arr);
                    break;
                case 'On Order Cost':
                    indexData[key].onOrderCost = addToArray(indexData[key].onOrderCost, arr);
                    break;
                case 'Inflow Updated':
                    indexData[key].inflowUpdated = addToArray(indexData[key].inflowUpdated, arr);
                    break;
                case 'Inflow Plan Retail':
                    indexData[key].inflowPlan = addToArray(indexData[key].inflowPlan, arr);
                    break;
                case 'Inflow Plan Cost':
                    indexData[key].inflowCostPlan = addToArray(indexData[key].inflowCostPlan, arr);
                    break;
                case 'Inventory BOM Plan NM':
                    indexData[key].inventoryBomPlanNM = addToArray(indexData[key].inventoryBomPlanNM, arr);
                    break;
                case 'Markdowns Remaining':
                    indexData[key].markdownsRemaining = addToArray(indexData[key].markdownsRemaining, arr);
                    break;
                case 'Current Inventory':
                    indexData[key].inventoryCurrent = addToArray(indexData[key].inventoryCurrent, arr);
                    break;
                case 'Inventory Deviation':
                    indexData[key].invDeviation = addToArray(indexData[key].invDeviation, arr);
                    break;
                case 'Inventory Deviation Cost':
                    indexData[key].invDeviationCost = addToArray(indexData[key].invDeviationCost, arr);
                    break;
                default:
                    break;
            }
        }
    }

    // Second pass: Calculate OTB values at the class level
    for (let item of parsed) {
        if (item.locationCode == '' || item.classCode == '') {
            continue;
        }

        // Only calculate at the most granular level (Location + Class)
        const classKey = `Loc${item.locationCode}|${item.classCode}`;
        const calcItem = indexData[classKey];

        if (!calcItem || item.vertical !== 'sales') continue; // Only process once per location+class combo

        const trend = calcItem.trend || 0;
        const costComplementMtd = item.costComplementMtd ?? 1;

        // Calculate OTB values for all 12 months
        for (let i = 0; i < 12; i++) {
            // OTB Trend calculation
            if (i === 0) {
                const inflowTrendInputs = {
                    invBomNM: calcItem.inventoryBomPlanNM[i] || 0,
                    currInv: calcItem.inventoryCurrent[i] || 0,
                    sales: calcItem.sales[i] || 0,
                    salesPlan: calcItem.salesPlan[i] || 0,
                    mdRemaining: calcItem.markdownsRemaining[i] || 0,
                    trend: trend,
                };
                calcItem.inflowTrendRetail[i] = Number(ReportService.calculateInflowTrend({ ...inflowTrendInputs, format: false }));
                calcItem.inflowTrendCost[i] = ReportService.calculateInflowTrendCost({ ...inflowTrendInputs, costComplementMtd });
                calcItem.otbTrendRetail[i] = Number(ReportService.calculateTrendedOtb({ ...inflowTrendInputs, onOrder: calcItem.onOrder[i] || 0, format: false }));
                calcItem.otbTrendCost[i] = ReportService.calculateTrendedOtbCost({ ...inflowTrendInputs, onOrderCost: calcItem.onOrderCost[i] || 0, costComplementMtd });
            } else {
                const inflowPlan = calcItem.inflowPlan[i] || 0;
                const onOrderRetail = calcItem.onOrder[i] || 0;
                const onOrderCost = calcItem.onOrderCost[i] || 0;
                calcItem.otbTrendRetail[i] = inflowPlan - onOrderRetail;
                calcItem.otbTrendCost[i] = ((calcItem.inflowCostPlan[i] || 0)) - onOrderCost;
            }

            // OTB Updated calculation
            if (i === 0) {
                const onOrderCost = calcItem.onOrderCost[i] || 0;
                const onOrderRetail = calcItem.onOrder[i] || 0;
                const inflowUpdated = calcItem.inflowUpdated[i] || 0;
                calcItem.otbUpdatedRetail[i] = inflowUpdated - onOrderRetail;
                calcItem.otbUpdatedCost[i] = (inflowUpdated * costComplementMtd) - onOrderCost;
            } else {
                const inflowPlan = calcItem.inflowPlan[i] || 0;
                const onOrderRetail = calcItem.onOrder[i] || 0;
                const onOrderCost = calcItem.onOrderCost[i] || 0;
                calcItem.otbUpdatedRetail[i] = inflowPlan - onOrderRetail;
                calcItem.otbUpdatedCost[i] = ((calcItem.inflowCostPlan[i] || 0)) - onOrderCost;
            }
        }

        // Now aggregate these calculated values up to all hierarchy levels
        const aggregationKeys = [
            `Loc${item.locationCode}`,
            `${item.classCode}`,
            `Cat${item.category}`,
            `Loc${item.locationCode}|Cat${item.category}`,
            `${item.classCode}|Loc${item.locationCode}`,
            `Cat${item.category}|Loc${item.locationCode}`,
            `Cat${item.category}|${item.classCode}`,
            `Cat${item.category}|${item.classCode}|Loc${item.locationCode}`,
            `|||`,
        ];

        for (const aggKey of aggregationKeys) {
            if (indexData[aggKey]) {
                indexData[aggKey].inflowTrendRetail = addToArray(indexData[aggKey].inflowTrendRetail, calcItem.inflowTrendRetail);
                indexData[aggKey].inflowTrendCost = addToArray(indexData[aggKey].inflowTrendCost, calcItem.inflowTrendCost);
                indexData[aggKey].otbTrendRetail = addToArray(indexData[aggKey].otbTrendRetail, calcItem.otbTrendRetail);
                indexData[aggKey].otbTrendCost = addToArray(indexData[aggKey].otbTrendCost, calcItem.otbTrendCost);
                indexData[aggKey].otbUpdatedRetail = addToArray(indexData[aggKey].otbUpdatedRetail, calcItem.otbUpdatedRetail);
                indexData[aggKey].otbUpdatedCost = addToArray(indexData[aggKey].otbUpdatedCost, calcItem.otbUpdatedCost);
            }
        }
    }

    return indexData;
}

// Helper: sort and filter data (stub, should be implemented in ReportService)
function sortAndFilterOtbRecapData(
    data: OtbRecapParsedData[],
    hierarchy: string[],
    locationFilter: string[],
    classFilter: string[],
    categoryFilter: string[]
) {
    // If you have a ReportService.sortAndFilterData for OTB, use it here. Otherwise, just return data for now.
    if (OtbRecapReportService.sortAndFilterData) {
        return OtbRecapReportService.sortAndFilterData(data, hierarchy, locationFilter, classFilter, categoryFilter, false);
    }
    return data;
}

function filterOtbRecapRawData(
    data: OtbRecapParsedData[],
    locationFilter: string[],
    classFilter: string[],
    categoryFilter: string[]
) {
    return data.filter(item => {
        const matchesLocation = locationFilter.length === 0 || locationFilter.includes(item.locationCode);
        const matchesClass = classFilter.length === 0 || classFilter.includes(item.classCode);
        const matchesCategory = categoryFilter.length === 0 || (item.category !== undefined && categoryFilter.includes(item.category));
        return matchesLocation && matchesClass && matchesCategory;
    });
}

const EOM_VERTS = [
    'Sales Plan',
    'Markdowns Plan',
    'Inventory BOM Plan',
    'Inflow Plan Retail',
    'On Order Retail EOM',
    'Inventory Deviation',
    'OTB Retail',
    'Cum. OTB',
];

const MTD_VERTS = [
    'Sales Plan',
    'Markdowns Plan',
    'Inventory BOM Plan',
    'Inflow Plan Retail',
    'On Order Retail',
    'Inflow Trend',
    'Inflow Updated',
    'OTB Trend',
    'OTB Updated',
    'Cum. OTB Trend',
    'Cum. OTB Updated',
];

// "Base" verticals are what the filter UI shows. Cost/Retail is derived from Pricing Type.
// We hide Retail/Cost variants in the filter UI, but we MUST keep their base names.
const EOM_VERTS_BASE = [
    'Sales Plan',
    'Markdowns Plan',
    'Inventory BOM Plan',
    'Inflow Plan',
    'On Order EOM',
    'Inventory Deviation',
    'OTB',
    'Cum. OTB',
];

const MTD_VERTS_BASE = [
    'Sales Plan',
    'Markdowns Plan',
    'Inventory BOM Plan',
    'Inflow Plan',
    'On Order',
    'Inflow Trend',
    'Inflow Updated',
    'OTB Trend',
    'OTB Updated',
    'Cum. OTB Trend',
    'Cum. OTB Updated',
];

const AUTO_UNCHECK_BASE_VERTS = [
    'Inflow Trend',
    'Inflow Updated',
];

const COST_MAPPING: { [key: string]: string } = {
    'OTB Retail': 'OTB Cost',
    'Cum. OTB': 'Cum. OTB Cost',
    'OTB Trend': 'OTB Cost Trend',
    'OTB Updated': 'OTB Cost Updated',
    'Cum. OTB Trend': 'Cum. OTB Cost Trend',
    'Cum. OTB Updated': 'Cum. OTB Cost Updated',
    'On Order Retail': 'On Order Cost',
    'Inflow Plan Retail': 'Inflow Plan Cost',
    'Inflow Trend': 'Inflow Cost Trend',
    'Inflow Updated': 'Inflow Cost Updated',
    'On Order Retail EOM': 'On Order Cost EOM',
    'Inventory Deviation': 'Inventory Deviation Cost',
};

// Helper to calculate cumulative OTB rows after tableData is built
function calculateCumulativeRows(
    tableRows: string[][],
    hierarchyColumns: any[],
    readableMonths: string[],
    isCost: boolean = false
) {
    // Define cumulative row configs
    const cumulativeConfigs = isCost
        ? [
            { label: 'Cum. OTB Cost', source: 'OTB Cost' },
            { label: 'Cum. OTB Cost Trend', source: 'OTB Cost Trend' },
            { label: 'Cum. OTB Cost Updated', source: 'OTB Cost Updated' }
        ]
        : [
            { label: 'Cum. OTB', source: 'OTB Retail' },
            { label: 'Cum. OTB Trend', source: 'OTB Trend' },
            { label: 'Cum. OTB Updated', source: 'OTB Updated' }
        ];

    // Clone to avoid mutating original
    const updatedRows = tableRows.map(row => [...row]);
    const sourceRowByGroup = new Map<string, number[]>();
    const monthStartIdx = hierarchyColumns.length;
    const sourceLabels = new Set(cumulativeConfigs.map(cfg => cfg.source));

    for (let rowIndex = 0; rowIndex < updatedRows.length; rowIndex++) {
        const row = updatedRows[rowIndex];
        const lastLabel = row[hierarchyColumns.length - 1];
        const groupKey = row.slice(0, Math.max(0, hierarchyColumns.length - 1)).join('|');

        if (sourceLabels.has(lastLabel)) {
            sourceRowByGroup.set(`${groupKey}|${lastLabel}`, row.slice(monthStartIdx, monthStartIdx + readableMonths.length).map(cell => parseFloat(cell.replace(/[^0-9.-]+/g, "")) || 0));
            continue;
        }

        // Find config for this row
        const config = cumulativeConfigs.find(cfg => cfg.label === lastLabel);
        if (!config) continue;

        const sourceMonths = sourceRowByGroup.get(`${groupKey}|${config.source}`);
        if (!sourceMonths) continue;

        let cumulative = 0;
        const monthData: string[] = [];

        for (let i = 0; i < readableMonths.length; i++) {
            cumulative += sourceMonths[i] || 0;
            monthData.push(ReportService.formatCurrency(cumulative));
        }
        // Replace month data in the row
        for (let i = 0; i < readableMonths.length; i++) {
            row[hierarchyColumns.length + i] = monthData[i];
        }
    }
    return updatedRows;
}
const TODAY = EpochMonthService.getTodayEpochMonth();
const MIN_SOURCE_PLAN_MONTH = TODAY - 11;
const MAX_SOURCE_PLAN_MONTH = TODAY;

function clampSourcePlanMonth(epochMonth: number): number {
    return Math.min(MAX_SOURCE_PLAN_MONTH, Math.max(MIN_SOURCE_PLAN_MONTH, epochMonth));
}

const OtbRecapTableReport = ({ clientId, epochMonth, monthVersion }: OtbRecapTableReportProps) => {
    const globalTip = TipContext.useMessage();
    const initialSourcePlanMonth = clampSourcePlanMonth(epochMonth);
    const [sourcePlanEpochMonth, setSourcePlanEpochMonth] = useState<number>(initialSourcePlanMonth);
    const [fromEpochMonth, setFromEpochMonth] = useState<number>(initialSourcePlanMonth);
    const [toEpochMonth, setToEpochMonth] = useState<number>(Math.min(initialSourcePlanMonth + 6, initialSourcePlanMonth + 11));
    const [reportEpochMonth, setReportEpochMonth] = useState<number>(TODAY);
    const [customTrend, setCustomTrend] = useState<number>(0);
    const [parsed, setParsed] = useState<OtbRecapParsedData[]>([]);
    const [sortedData, setSortedData] = useState<OtbRecapParsedData[]>([]);
    const [loading, setLoading] = useState(false);
    const [hierarchy, setHierarchy] = useState<string[] | null>(["vertical", "locationCode", "classCode"]);
    // Filters
    const [locationFilter, setLocationFilter] = useState<string[]>([]);
    const [classFilter, setClassFilter] = useState<string[]>([]);
    const [categoryFilter, setCategoryFilter] = useState<string[]>([]);
    const dataRef = useRef<OtbRecapRawData[]>([]);
    const indexCalcData = useRef<IndexCalcData>({});

    // Initialize displayedVerts and trendType based on monthVersion prop
    const initialTrendType = monthVersion === 'MONTH_END' ? 'EOM' : 'MTD';

    const [displayedVerts, setDisplayedVerts] = useState<string[]>(monthVersion === 'MONTH_END' ? EOM_VERTS : MTD_VERTS);
    const [trendType, setTrendType] = useState<'MTD' | 'EOM'>(initialTrendType);
    const [pricingType, setPricingType] = useState<'Retail' | 'Cost'>('Retail');
    const [displayMode, setDisplayMode] = useState<'Detailed' | 'Basic'>('Detailed');

    const [verticalFilter, setVerticalFilter] = useState<string[]>(['ALL']);
    const requestedReportEpochMonth = sourcePlanEpochMonth;
    const maxFromEpochMonth = sourcePlanEpochMonth + 11;
    const shouldAutoUncheckInflowRows = fromEpochMonth > TODAY;
    const previousShouldAutoUncheckRef = useRef(shouldAutoUncheckInflowRows);

    // Helper to decide which single vertical to show in Basic mode
    function computeBasicModeVertical(trend: 'MTD' | 'EOM', pricing: 'Retail' | 'Cost'): string {
        if (trend === 'EOM') {
            return pricing === 'Cost' ? 'OTB Cost' : 'OTB Retail';
        }
        // MTD
        return pricing === 'Cost' ? 'OTB Cost Updated' : 'OTB Updated';
    }

    function getDefaultBaseVertsForTrend(trend: 'MTD' | 'EOM'): string[] {
        return trend === 'MTD' ? [...MTD_VERTS_BASE] : [...EOM_VERTS_BASE];
    }

    function mapBaseVertToDisplayedVert(baseVert: string, pricing: 'Retail' | 'Cost'): string {
        // Translate UI base names into the actual vertical label present in the dataset.
        // Note: some verticals encode Retail in the name, and get mapped to Cost via COST_MAPPING.
        const retailName = (() => {
            switch (baseVert) {
                case 'OTB':
                    return 'OTB Retail';
                case 'Inflow Plan':
                    return 'Inflow Plan Retail';
                case 'On Order':
                    return 'On Order Retail';
                case 'On Order EOM':
                    return 'On Order Retail EOM';
                default:
                    return baseVert;
            }
        })();

        return pricing === 'Cost' ? (COST_MAPPING[retailName] || retailName) : retailName;
    }

    // When toggling MTD/EOM, reset datapoint filter back to that mode's default.
    useEffect(() => {
        if (displayMode === 'Basic') return; // no filtering in Basic
        setVerticalFilter(['ALL']);
    }, [trendType]);

    useEffect(() => {
        const wasAutoUncheckEnabled = previousShouldAutoUncheckRef.current;
        previousShouldAutoUncheckRef.current = shouldAutoUncheckInflowRows;

        if (!shouldAutoUncheckInflowRows || wasAutoUncheckEnabled) {
            return;
        }

        setVerticalFilter((current) => {
            const defaultBaseVerts = getDefaultBaseVertsForTrend(trendType);
            const nextSelection = current.includes('ALL') ? defaultBaseVerts : current;
            const filtered = nextSelection.filter(v => !AUTO_UNCHECK_BASE_VERTS.includes(v));

            return filtered.length ? filtered : ['ALL'];
        });
    }, [shouldAutoUncheckInflowRows, trendType]);

    const vertFilterOptions = useMemo(() => {
        const base = getDefaultBaseVertsForTrend(trendType);
        return base.map(v => ({ code: v, label: v }));
    }, [trendType]);

    const selectedBaseVerts = useMemo(() => {
        if (displayMode === 'Basic') return getDefaultBaseVertsForTrend(trendType);
        if (verticalFilter.includes('ALL')) return getDefaultBaseVertsForTrend(trendType);
        return verticalFilter;
    }, [verticalFilter, trendType, displayMode]);

    // Drive displayedVerts purely from state (trendType, pricingType, displayMode, verticalFilter)
    useEffect(() => {
        if (displayMode === 'Basic') {
            setDisplayedVerts([computeBasicModeVertical(trendType, pricingType)]);
            return;
        }

        const mapped = selectedBaseVerts.map(v => mapBaseVertToDisplayedVert(v, pricingType));
        setDisplayedVerts(mapped);
    }, [trendType, pricingType, displayMode, selectedBaseVerts]);

    useEffect(() => {
        const nextSourcePlanMonth = clampSourcePlanMonth(epochMonth);
        setSourcePlanEpochMonth(nextSourcePlanMonth);
        setFromEpochMonth(nextSourcePlanMonth);
        setToEpochMonth(Math.min(nextSourcePlanMonth + 6, nextSourcePlanMonth + 11));
    }, [epochMonth]);

    useEffect(() => {
        if (fromEpochMonth < sourcePlanEpochMonth) {
            setFromEpochMonth(sourcePlanEpochMonth);
            return;
        }

        if (fromEpochMonth > maxFromEpochMonth) {
            setFromEpochMonth(maxFromEpochMonth);
        }
    }, [sourcePlanEpochMonth, fromEpochMonth, maxFromEpochMonth]);

    useEffect(() => {
        if (toEpochMonth < fromEpochMonth) {
            setToEpochMonth(fromEpochMonth);
            return;
        }

        if (toEpochMonth > maxFromEpochMonth) {
            setToEpochMonth(maxFromEpochMonth);
        }
    }, [toEpochMonth, fromEpochMonth, maxFromEpochMonth]);

    useEffect(() => {
        setParsed([]);
        setSortedData([]);
        dataRef.current = [];
        indexCalcData.current = {};
    }, [sourcePlanEpochMonth, clientId]);

    const readableMonths = useMemo(() => {
        let months = [];
        for (let m = fromEpochMonth; m <= toEpochMonth; m++) {
            months.push(EpochMonthService.epochMonthToMMMM(m));
        }
        return months;
    }, [fromEpochMonth, toEpochMonth]);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                // Fetch once per client/source plan month; From/To only adjust local display window.
                const response = await ReportService.getOtbRecapReportData({
                    clientId,
                    startEpochMonth: sourcePlanEpochMonth,
                    endEpochMonth: sourcePlanEpochMonth + 11,
                });
                const rawData = response?.data || [];
                setReportEpochMonth(response?.reportEpochMonth ?? requestedReportEpochMonth);
                dataRef.current = rawData;

                // if data empty, skip further processing
                if (rawData.length === 0) {
                    setParsed([]);
                    setSortedData([]);
                    setLoading(false);
                    return;
                }

                // get max trend from rawData
                let maxTrend = 0;
                rawData.forEach((item: OtbRecapRawData) => {
                    if (item.trend && item.trend > maxTrend) {
                        maxTrend = item.trend;
                    }
                });
                setCustomTrend(maxTrend);
                const parsedData = parseOtbRecapRawData(rawData);
                setParsed(parsedData);

                // Build index calculation data
                indexCalcData.current = buildIndexCalcData(parsedData, maxTrend);

                setSortedData(parsedData);
            } catch (e) {
                console.error(e);
                if (globalTip) globalTip.show('Failed to load OTB Recap');
            } finally { setLoading(false); }
        };
        fetchData();
    }, [clientId, sourcePlanEpochMonth, requestedReportEpochMonth]);

    useEffect(() => {
        const filteredParsed = filterOtbRecapRawData(parsed, locationFilter, classFilter, categoryFilter);
        indexCalcData.current = buildIndexCalcData(filteredParsed, customTrend);

        // Re-filter/sort data
        let _hierarchy = hierarchy;
        if (!_hierarchy || _hierarchy.length === 0) _hierarchy = ["vertical", "locationCode", "classCode"];
        let filtered = sortAndFilterOtbRecapData(parsed, _hierarchy, locationFilter, classFilter, categoryFilter);
        setSortedData(filtered);
    }, [parsed, customTrend, locationFilter, classFilter, categoryFilter]);

    // Filter/sort/group data when filters or hierarchy change
    useEffect(() => {
        let _hierarchy = hierarchy;
        if (!_hierarchy || _hierarchy.length === 0) _hierarchy = ["vertical", "locationCode", "classCode"];

        let filtered = sortAndFilterOtbRecapData(parsed, _hierarchy, locationFilter, classFilter, categoryFilter);
        setSortedData(filtered);
    }, [parsed, hierarchy, locationFilter, classFilter, categoryFilter]);

    // Build hierarchyColumns dynamically, removing vertical when in Basic mode
    let effectiveHierarchy = hierarchy ? [...hierarchy] : [];
    if (displayMode === 'Basic') {
        effectiveHierarchy = effectiveHierarchy.filter(h => h !== 'vertical');
    }

    let hierarchyColumns: any[] = [];
    for (let i = 0; i < effectiveHierarchy.length; i++) {
        hierarchyColumns.push({
            name: "",
            sortable: false,
            customClassName: i === 0
                ? "px-4 py-2 bg-gray-500 text-white"
                : "px-4 py-2 bg-purple-100 border-purple-700 text-purple-800"
        });
    }

    const monthColumns = readableMonths.map((month) => ({
        name: month,
        sortable: false,
        customClassName: "px-4 py-2"
    }));

    const totalColumn = {
        name: "Total",
        sortable: false,
        customClassName: "px-4 py-2 bg-slate-200 font-bold"
    };

    const columns = [
        ...hierarchyColumns,
        ...monthColumns,
        totalColumn
    ];

    const tableData = useMemo(() => {
        const isCost = pricingType === 'Cost';
        const monthOffset = Math.max(0, fromEpochMonth - reportEpochMonth);
        let calcItem = null, key = null;

        const getMoneyItemByDataIndex = (item: OtbRecapParsedData, dataIndex: number) => {
            if (dataIndex < 0 || dataIndex >= 12) {
                return null;
            }

            const monthKey = `month${dataIndex + 1}`;
            const value = (item as any)[monthKey];
            return value instanceof MoneyDataItem ? value : null;
        };

        const getCalcValueByDataIndex = (values: number[], dataIndex: number) => {
            if (dataIndex < 0 || dataIndex >= values.length) {
                return null;
            }

            return values[dataIndex] ?? 0;
        };

        function buildKey(item: OtbRecapParsedData, hierarchyArr: string[]): string {
            const parts = hierarchyArr
                .filter(h => h !== 'vertical')
                .map(h => {
                    if (h === 'locationCode') {
                        const v = (item as any)[h];
                        return v ? `Loc${v}` : '';
                    }
                    if (h === 'category') {
                        const v = (item as any)[h];
                        return v ? `Cat${v}` : '';
                    }
                    // classCode and other plain fields
                    return (item as any)[h] ?? '';
                })
                .filter(p => p !== '');

            return parts.join('|');
        }

        const fullHierarchyForLabels = hierarchy ?? ["vertical", "locationCode", "classCode"]; // keep vertical for internal logic

        const baseRows = sortedData.map((item) => {
            const labelRows = OtbRecapReportService.hierarchyItemToVerticalDisplayNames(item, fullHierarchyForLabels);
            const lastLabel = labelRows[labelRows.length - 1] ?? '';

            // Total row
            if (!item.locationCode && !item.classCode && !item.category) {
                key = `|||`;
            } else {
                key = buildKey(item, fullHierarchyForLabels);
            }

            calcItem = indexCalcData.current[key];

            if (!calcItem) {
                if (item.classCode) {
                    const classOnlyKey = `${item.classCode}`;
                    calcItem = indexCalcData.current[classOnlyKey] ?? calcItem;
                }
            }
            if (!calcItem) {
                if (item.locationCode) {
                    const locOnlyKey = `Loc${item.locationCode}`;
                    calcItem = indexCalcData.current[locOnlyKey] ?? calcItem;
                }
            }
            if (!calcItem && item.category) {
                const catOnlyKey = `Cat${item.category}`;
                calcItem = indexCalcData.current[catOnlyKey] ?? calcItem;
            }

            if (!calcItem) return null;

            let monthData: string[] = [];
            switch (lastLabel) {
                case 'OTB Retail':
                case 'OTB Cost':
                    for (let i = 0; i < readableMonths.length; i++) {
                        const dataIndex = monthOffset + i;
                        const moneyItem = getMoneyItemByDataIndex(item, dataIndex);
                        if (dataIndex > 0) {
                            // Guard against missing month fields during transitions
                            monthData.push(moneyItem instanceof MoneyDataItem ? moneyItem.formatted : '');
                            continue;
                        }
                        const baseValue = moneyItem instanceof MoneyDataItem ? Number(moneyItem.value ?? 0) : 0;
                        monthData.push(
                            ReportService.formatCurrency(
                                baseValue + (isCost ? (getCalcValueByDataIndex(calcItem.invDeviationCost, dataIndex) || 0) : (getCalcValueByDataIndex(calcItem.invDeviation, dataIndex) || 0))
                            )
                        );
                    }
                    break;
                case 'OTB Cost Updated':
                case 'OTB Updated':
                    // Use pre-calculated values from indexCalcData
                    for (let i = 0; i < readableMonths.length; i++) {
                        const dataIndex = monthOffset + i;
                        const value = isCost ? getCalcValueByDataIndex(calcItem.otbUpdatedCost, dataIndex) : getCalcValueByDataIndex(calcItem.otbUpdatedRetail, dataIndex);
                        monthData.push(value === null ? '' : ReportService.formatCurrency(value));
                    }
                    break;
                case 'OTB Cost Trend':
                case 'OTB Trend':
                    // Use pre-calculated values from indexCalcData
                    for (let i = 0; i < readableMonths.length; i++) {
                        const dataIndex = monthOffset + i;
                        const value = isCost ? getCalcValueByDataIndex(calcItem.otbTrendCost, dataIndex) : getCalcValueByDataIndex(calcItem.otbTrendRetail, dataIndex);
                        monthData.push(value === null ? '' : ReportService.formatCurrency(value));
                    }
                    break;
                case 'Inflow Trend':
                case 'Inflow Cost Trend':
                    if (calcItem) {
                        for (let i = 0; i < readableMonths.length; i++) {
                            const dataIndex = monthOffset + i;
                            if (dataIndex !== 0) {
                                monthData.push('');
                                continue;
                            }
                            // Pre-calculated per location + class in buildIndexCalcData, same source as OTB Trend
                            const inflowTrend = isCost ? getCalcValueByDataIndex(calcItem.inflowTrendCost, dataIndex) : getCalcValueByDataIndex(calcItem.inflowTrendRetail, dataIndex);
                            monthData.push(inflowTrend === null ? '' : ReportService.formatCurrency(inflowTrend));
                        }
                    }
                    break;
                default:
                    for (let i = 0; i < readableMonths.length; i++) {
                        const dataIndex = monthOffset + i;
                        const moneyItem = getMoneyItemByDataIndex(item, dataIndex);
                        monthData.push(moneyItem instanceof MoneyDataItem ? moneyItem.formatted : '');
                    }
            }

            if (!displayedVerts.includes(lastLabel)) return null;

            return [...labelRows, ...monthData];
        }).filter(r => r !== null) as string[][];

        let rowsWithCumulative = calculateCumulativeRows(baseRows, hierarchyColumns, readableMonths, pricingType === 'Cost');

        // Basic mode filtering & removing vertical column
        if (displayMode === 'Basic') {
            const verticalWanted = computeBasicModeVertical(trendType, pricingType);
            const vIdx = fullHierarchyForLabels.length - 1;
            rowsWithCumulative = rowsWithCumulative
                .filter(r => r[vIdx] === verticalWanted)
                .map(r => {
                    const clone = [...r];
                    clone.splice(vIdx, 1); // remove vertical column cell
                    return clone;
                });
        }

        // Calculate totals AFTER all transformations are complete
        const rowsWithTotals = rowsWithCumulative.map(row => {
            // The month data starts after hierarchy columns
            const monthStartIdx = hierarchyColumns.length;
            const lastLabel = row[monthStartIdx - 1] ?? '';

            if (['Cum. OTB', 'Cum. OTB Trend', 'Cum. OTB Updated', 'Cum. OTB Cost', 'Cum. OTB Cost Trend', 'Cum. OTB Cost Updated'].includes(lastLabel)) {
                const previousMonthValue = row[row.length - 1] ?? '';
                return [...row, previousMonthValue];
            }

            const inventoryPlanLabels = new Set([
                'Inventory BOM Plan',
                'Inventory BOM Plan NM',
            ]);

            if (inventoryPlanLabels.has(lastLabel)) {
                const monthValues = row
                    .slice(monthStartIdx)
                    .map(cell => parseFloat((cell ?? '').toString().replace(/[^0-9.-]+/g, '')) || 0);
                const monthCount = monthValues.length > 0 ? monthValues.length : 1;
                const average = monthValues.reduce((sum, value) => sum + value, 0) / monthCount;
                return [...row, ReportService.formatCurrency(average)];
            }

            let total = 0;
            for (let i = monthStartIdx; i < row.length; i++) {
                const numValue = parseFloat(row[i].replace(/[^0-9.-]+/g, "")) || 0;
                total += numValue;
            }

            const totalFormatted = ReportService.formatCurrency(total);
            return [...row, totalFormatted];
        });

        return rowsWithTotals;
    }, [sortedData, hierarchy, displayedVerts, fromEpochMonth, toEpochMonth, pricingType, trendType, displayMode, readableMonths, customTrend, reportEpochMonth]);

    const rowClassName = (rowIndex: number) => {
        if (!tableData || rowIndex < 0 || rowIndex >= tableData.length) return null;
        // Use effectiveHierarchy (without vertical in Basic mode)
        return OtbRecapReportService.getRowClassName(tableData[rowIndex], effectiveHierarchy.length ? effectiveHierarchy : ["vertical", "locationCode", "classCode"]);
    };

    return (
        <div className="w-full h-full flex flex-col items-center">
            <h2 className="text-2xl font-bold mb-2">OTB Recap</h2>
            {loading ? <p>Loading...</p> : (
                <div className="w-full h-full">
                    <BaseReport
                        title="OTB Recap"
                        tableColumns={columns.map(c => c.name)}
                        tableData={tableData}
                        fileName={`OTB_Recap_${new Date().toLocaleDateString()}`}
                        hierarchy={hierarchy}
                        setHierarchy={setHierarchy}
                        hierarchyOptions={HIERARCHY_OPTIONS}
                        customTrend={customTrend}
                        setCustomTrend={setCustomTrend}
                        parsedData={parsed}
                        filteredLocationCodes={locationFilter}
                        filteredClassCodes={classFilter}
                        filteredCategories={categoryFilter}
                        setFilteredLocationCodes={setLocationFilter}
                        setFilteredClassCodes={setClassFilter}
                        setFilteredCategories={setCategoryFilter}
                        onApplyFilters={() => {
                            // Reparse
                            let parsedData = parseOtbRecapRawData(dataRef.current);
                            setParsed(parsedData);
                        }}
                        hasSecondaryToolbar={true}
                    />
                    <div className="flex flex-row flex-wrap items-center space-x-4 mb-4 ml-8">
                        <div className="flex flex-col items-start">
                            <span className="text-xs font-semibold text-slate-600">Source Plan Month</span>
                            <MonthSelector
                                epochMonth={sourcePlanEpochMonth}
                                setEpochMonth={setSourcePlanEpochMonth}
                                minMonth={MIN_SOURCE_PLAN_MONTH}
                                maxMonth={MAX_SOURCE_PLAN_MONTH}
                            />
                        </div>
                        <div className="flex flex-col items-start">
                            <span className="text-xs font-semibold text-slate-600">From</span>
                            <MonthSelector
                                epochMonth={fromEpochMonth}
                                setEpochMonth={setFromEpochMonth}
                                minMonth={sourcePlanEpochMonth}
                                maxMonth={sourcePlanEpochMonth + 11}
                            />
                        </div>
                        <div className="flex flex-col items-start">
                            <span className="text-xs font-semibold text-slate-600">To</span>
                            <MonthSelector
                                epochMonth={toEpochMonth}
                                setEpochMonth={setToEpochMonth}
                                minMonth={fromEpochMonth}
                                maxMonth={sourcePlanEpochMonth + 11}
                            />
                        </div>
                        <div className="flex flex-col items-start">
                            <span className="text-xs font-semibold text-slate-600">Trend Type</span>
                            <select
                                value={trendType}
                                onChange={e => setTrendType(e.target.value === 'MTD' ? 'MTD' : 'EOM')}
                                className="h-10 px-4 border border-[#e1edf1] rounded-md text-[13px] font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-sky-200"
                            >
                                <option value="MTD">MTD</option>
                                <option value="EOM">EOM</option>
                            </select>
                        </div>
                        <div className="flex flex-col items-start">
                            <span className="text-xs font-semibold text-slate-600">Pricing Type</span>
                            <select
                                value={pricingType}
                                onChange={e => setPricingType(e.target.value === 'Retail' ? 'Retail' : 'Cost')}
                                className="h-10 px-4 border border-[#e1edf1] rounded-md text-[13px] font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-sky-200"
                            >
                                <option value="Retail">Retail</option>
                                <option value="Cost">Cost</option>
                            </select>
                        </div>
                        <div className="flex flex-col items-start">
                            <span className="text-xs font-semibold text-slate-600">Display Mode</span>
                            <select
                                value={displayMode}
                                onChange={e => setDisplayMode(e.target.value === 'Basic' ? 'Basic' : 'Detailed')}
                                className="h-10 px-4 border border-[#e1edf1] rounded-md text-[13px] font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-sky-200"
                            >
                                <option value="Detailed">Detailed</option>
                                <option value="Basic">Basic</option>
                            </select>
                        </div>

                        {/* Data-points filter (Detailed mode only) */}
                        {displayMode === 'Detailed' && (
                            <div className="flex flex-col items-start">
                                <span className="text-xs font-semibold text-slate-600">Data Points</span>
                                <MultiSelect
                                    options={vertFilterOptions}
                                    value={verticalFilter}
                                    onChange={setVerticalFilter}
                                    placeholder="All"
                                    maxHeight={320}
                                />
                            </div>
                        )}
                    </div>
                    <ReportTable
                        columns={columns}
                        data={tableData}
                        rowClassName={rowClassName}
                        defaultSortColumn=""
                        defaultSortDirection="asc"
                        headerClassName="bg-slate-700 !text-white !text-center py-2 px-4"
                        toPdf={() => (window as any).BaseReportExport && (window as any).BaseReportExport('pdf')}
                        toCsv={() => (window as any).BaseReportExport && (window as any).BaseReportExport('csv')}
                        stickyHeader
                        virtualize={true}
                        batchSize={300}
                    />
                </div>
            )}
        </div>
    );
};

interface OtbRecapTableReportModalProps {
    clientId: string | number;
    epochMonth: number | null;
    onClose: () => void;
    monthVersion?: 'MONTH_TO_DATE' | 'MONTH_END';
}

const OtbRecapTableReportModal = ({ clientId, epochMonth, onClose, monthVersion }: OtbRecapTableReportModalProps) => (
    <ReportModal onClose={onClose}>
        <OtbRecapTableReport
            clientId={clientId}
            epochMonth={epochMonth ?? EpochMonthService.getTodayEpochMonth()}
            monthVersion={monthVersion}
        />
    </ReportModal>
);

export { OtbRecapTableReport, OtbRecapTableReportModal };
